/**
 * BannerBundle entity class
 */

import { PLATFORM_TYPES } from 'app/core/constants'
import { clone } from 'app/helpers/object'
import { getUniqueLink } from 'app/helpers/string'

/**
 * BannerBundleFactory
 * returns BannerBundle entity class
 *
 * @class BannerBundleFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.BannerBundle} class
 *
 * @ngdoc factory
 * @name mwApp.factory:BannerBundleFactory
 */
/* @ngInject */
export default function BannerBundleFactory ($http, $sce, Type, Entity, authSvc, onOffMixin, FileUploadMixin, alertLiteSvc) {
  /* === BannerBundle class === */

  /**
   * BannerBundle model
   *
   * @class BannerBundle
   * @classdesc BannerBundle model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @return {models.BannerBundle} BannerBundle entity
   */
  function BannerBundle () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  BannerBundle.prototype = Object.create(Entity.prototype)
  BannerBundle.prototype.constructor = BannerBundle

  /* === BannerBundle instance methods & fields === */

  Object.assign(BannerBundle.prototype, {

    /**
     * instance fields
     *
     * @memberof models.BannerBundle#
     * @type {Object}
     */
    _fields: {
      id: 0,
      contentEnabled: Boolean,
      contentId: Number,
      contentName: String,
      contentSetEnabled: Boolean,
      contentType: String, // TYPES - TV_CHANNEL, TV_SHOW, MOVIE, CATEGORY, SUBCATEGORY
      contentUnassignedOrDeleted: Boolean,
      createdBy: String,
      createdTime: String,
      enabled: false,
      externalLink: String,
      inSubscriptions: [Number],
      mobileBannerUrl: String,
      name: String,
      networkId: Number,
      platformTypes: [], // PLATFORM_TYPES - STB, PC, iOS, Android, SamsungTV, Others, WEB
      resourceType: 'INTERNAL', // TYPES - INTERNAL, EXTERNAL, WITHOUT_LINK
      sortOrder: Number,
      tabletBannerUrl: String,
      tvBannerUrl: String,
      updatedBy: String,
      updatedTime: String,
      $carouselId: Number,
      $content: {
        id: Number,
        name: String,
        enabled: Boolean,
        contentType: String,
        unassignedOrDeleted: Boolean,
        landscapeImageUrl: String,
        portraitImageUrl: String
      }
    },

    /**
     * url template
     *
     * @memberof models.BannerBundle#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/subscriptions/carousels/banners/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.BannerBundle#
     * @type {String}
     */
    _publicUrlTemplate: 'networks/{{::networkId}}/subscriptions/carousels/banners/{{::carouselId}}/bundles/{{::id}}',

    /**
     * Prepare note
     *
     * @memberof models.BannerBundle#
     * @override
     */
    _prepare: function () {
      if (this.contentId) {
        this.$content = {
          id: this.contentId,
          name: this.contentName,
          enabled: this.contentEnabled
        }
      }

      this.tabletBannerUrl = getUniqueLink(this.tabletBannerUrl)
      this.mobileBannerUrl = getUniqueLink(this.mobileBannerUrl)
      this.tvBannerUrl = getUniqueLink(this.tvBannerUrl)
    },

    /**
     * Get underlying object
     *
     * @memberof models.BannerBundle#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      if (this.$content) {
        this.contentId = this.$content.id
        this.contentName = this.$content.name
        this.contentEnabled = this.$content.enabled
      }
      if (this.resourceType !== 'INTERNAL') {
        delete this.contentId
      }

      return Entity.prototype._object.apply(this, arguments)
    },

    /**
     * Get platforms array with checked flags, depending on network platforms array
     *
     * @memberof models.BannerBundle#
     * @param {String[]} def - default flags
     * @param {types[]} types - list of types
     * @return {Object[]}
     */
    _enhancePlatforms: function (def, types = []) {
      var ret = clone(PLATFORM_TYPES.filter((p) =>
        !(!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED') && p.name === 'WEB')))

      for (var i = ret.length; i--;) { // disable default checked items
        ret[i].checked = false
      }

      if (types.length) {
        ret = ret.filter((p) => types.indexOf(p.name) !== -1)
      }

      if (!this.platformTypes.length && def) {
        for (let i = ret.length; i--;) {
          if (def.indexOf(ret[i].name) !== -1) {
            ret[i].checked = true
          }
        }
      } else
      if (this.platformTypes && this.platformTypes.length) {
        for (let i = ret.length; i--;) {
          ret[i].checked = this.platformTypes.indexOf(ret[i].name) !== -1
        }
      }
      return ret
    },

    /**
     * Set network platforms array from platforms array with checked flags
     *
     * @memberof models.BannerBundle#
     * @param {Object[]} platforms - platforms array
     */
    _reducePlatforms: function (platforms) {
      this.platformTypes = []
      for (var i = platforms.length; i--;) {
        if (platforms[i].checked) {
          this.platformTypes.push(platforms[i].name)
        }
      }
      return this
    },

    /**
     * Patch method
     *
     * @memberof models.BannerBundle#
     * @param {String} url
     * @param params
     * @return {Promise} promise
     * @private
     */
    _patch: function (url, params) {
      return $http.patch(url, params)
        .then(
          response => {
            if (response.data) {
              this._weakCopy(response.data)
            }
            return response
          }
        ).catch(
          rejection => {
            return alertLiteSvc.alertError(rejection)
          })
    },

    /**
     * On/Off Entity, performing PATCH request to the server
     *
     * @memberof models.BannerBundle#
     * @param {Boolean} [enabled=this.enabled] - enable or disable Entity
     * @return {Promise} promise
     */
    _onOff: function (enabled) {
      if (typeof enabled !== 'boolean') {
        enabled = this.enabled
      }

      return this._patch(this._url + '/status', enabled ? '"ENABLE"' : '"DISABLE"')
        .catch(rejection => {
          this.enabled = !this.enabled
          return rejection
        })
    },

    /**
     * Get preview image URL
     *
     * @memberof models.BannerBundle#
     * @return {String}
     */
    _previewImageUrl: function () {
      return FileUploadMixin.getPreviewImageUrl(this.tabletBannerUrl)
    },

    /**
     * Get image URL for given size
     *
     * @memberof models.VOD#
     * @param  {String} fieldName
     * @param  {String} size
     * @return {String}
     */
    _urlFor: function (fieldName, size) {
      return this[fieldName]
        ? this[fieldName].indexOf('{{size}}') > -1
          ? this[fieldName].replace('{{size}}', size)
          : this[fieldName] + '?resize=' + size
        : ''
    }
  }, FileUploadMixin)

  /* === BannerBundle static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(BannerBundle, Entity)
  BannerBundle._name = 'BannerBundle'
  BannerBundle.init()

  // permissions
  BannerBundle.access = {
    view: 'viewBannerCarousel',
    edit: 'editBannerCarousel',
    create: 'editBannerCarousel',
    delete: 'deleteBannerCarousel'
  }

  return BannerBundle
}
